import React from "react";
import { cn } from "@/lib/utils";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronDown } from "lucide-react";
import { type NavItem, type SubMenuItem } from "./navigation-sections";

interface SidebarNavItemProps {
  item: NavItem;
  collapsed: boolean;
  activePath: string;
  expanded: boolean;
  onToggle: (key: string) => void;
}

const isPathActive = (
  activePath: string,
  path: string,
  submenu?: SubMenuItem[],
) => {
  if (submenu) {
    return submenu.some(
      (sub) => activePath === sub.path || activePath.startsWith(sub.path + "/"),
    );
  }
  return activePath === path;
};

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  collapsed,
  activePath,
  expanded,
  onToggle,
}) => {
  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const isActive = isPathActive(activePath, item.path, item.submenu);
  const isExactActive = activePath === item.path;
  const Icon = item.icon;

  const navItemContent = (
    <div
      data-testid={`nav-${item.key}`}
      onClick={(e) => {
        if (hasSubmenu && !collapsed) {
          e.preventDefault();
          onToggle(item.key);
        }
      }}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer",
        "transition-colors duration-150",
        collapsed && "justify-center",
        item.placeholder && "opacity-50",
        isExactActive
          ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
          : isActive
            ? "text-indigo-600 dark:text-indigo-400"
            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
      )}
    >
      <Icon
        className={cn(
          "w-5 h-5 flex-shrink-0",
          isActive || isExactActive
            ? "text-indigo-600 dark:text-indigo-400"
            : "",
        )}
        strokeWidth={1.5}
      />
      {!collapsed && (
        <>
          <span className="text-sm font-medium truncate flex-1">
            {item.label}
          </span>
          {hasSubmenu && (
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                expanded && "rotate-180",
              )}
            />
          )}
          {item.placeholder && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
              Soon
            </span>
          )}
        </>
      )}
    </div>
  );

  if (hasSubmenu) {
    return (
      <li>
        {collapsed ? (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <a href={item.submenu![0].path}>{navItemContent}</a>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={8}
                className={cn(
                  "py-2 rounded-md text-sm",
                  "bg-slate-900 dark:bg-slate-100",
                  "shadow-lg animate-fade-in z-50",
                )}
              >
                <div className="px-3 pb-1 font-semibold text-white dark:text-slate-900 border-b border-slate-700 dark:border-slate-300 mb-1">
                  {item.label}
                </div>
                {item.submenu!.map((sub) =>
                  sub.placeholder ? (
                    <button
                      key={sub.key}
                      type="button"
                      aria-disabled="true"
                      className={cn(
                        "block w-full text-left px-3 py-1.5 text-slate-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900",
                        activePath === sub.path &&
                          "text-indigo-400 dark:text-indigo-600 font-medium",
                        "opacity-50 cursor-not-allowed",
                      )}
                    >
                      {sub.label}
                    </button>
                  ) : (
                    <a
                      key={sub.key}
                      href={sub.path}
                      className={cn(
                        "block px-3 py-1.5 text-slate-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900",
                        activePath === sub.path &&
                          "text-indigo-400 dark:text-indigo-600 font-medium",
                      )}
                    >
                      {sub.label}
                    </a>
                  ),
                )}
                <Tooltip.Arrow className="fill-slate-900 dark:fill-slate-100" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ) : (
          <>
            {navItemContent}
            {expanded && (
              <ul className="mt-1 space-y-0.5">
                {item.submenu!.map((sub) => (
                  <li key={sub.key}>
                    {sub.placeholder ? (
                      <button
                        type="button"
                        aria-disabled="true"
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer pl-10 w-full text-left",
                          "transition-colors duration-150 text-sm font-medium",
                          "opacity-50 cursor-not-allowed",
                          activePath === sub.path
                            ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
                        )}
                      >
                        {sub.icon && (
                          <sub.icon
                            className={cn(
                              "w-4 h-4 flex-shrink-0",
                              activePath === sub.path
                                ? "text-indigo-600 dark:text-indigo-400"
                                : "",
                            )}
                            strokeWidth={1.5}
                          />
                        )}
                        <span className="text-[13px] truncate flex-1">
                          {sub.label}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                          Soon
                        </span>
                      </button>
                    ) : (
                      <a
                        href={sub.path}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer pl-10",
                          "transition-colors duration-150 text-sm font-medium",
                          activePath === sub.path
                            ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200",
                        )}
                      >
                        {sub.icon && (
                          <sub.icon
                            className={cn(
                              "w-4 h-4 flex-shrink-0",
                              activePath === sub.path
                                ? "text-indigo-600 dark:text-indigo-400"
                                : "",
                            )}
                            strokeWidth={1.5}
                          />
                        )}
                        <span className="text-[13px] truncate flex-1">
                          {sub.label}
                        </span>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </li>
    );
  }

  const wrappedItem = (
    <li>
      {item.placeholder ? (
        <button type="button" aria-disabled="true" className="w-full text-left">
          {navItemContent}
        </button>
      ) : (
        <a href={item.path}>{navItemContent}</a>
      )}
    </li>
  );

  if (collapsed) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{wrappedItem}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            sideOffset={8}
            className={cn(
              "px-3 py-1.5 rounded-md text-sm font-medium",
              "bg-slate-900 dark:bg-slate-100",
              "text-white dark:text-slate-900",
              "shadow-lg animate-fade-in z-50",
            )}
          >
            {item.label}
            <Tooltip.Arrow className="fill-slate-900 dark:fill-slate-100" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return wrappedItem;
};
